import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toastSuccess, toastError } from '../../utils/alertHelper';
import CommonModal from '../../components/commonComponent/CommonModal';
import axios from '../../utils/axiosInstance';

const IMAGE_BASE_URL = process.env.REACT_APP_Image_URL || "https://api.solarestimate.in/image/";

function Nav({ onMenuToggle }) {
  const navigate = useNavigate();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(""); // 'profile' or 'organization'
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [user, setUser] = useState(JSON.parse(localStorage.getItem("user") || "{}"));

  // Helper to check if a value is a File-like object
  const isFile = v =>
    v instanceof File ||
    (v && typeof v === "object" && typeof v.type === "string" && typeof v.size === "number");

  // Normalize image values coming from API/localStorage
  const normalizeApiPathValue = (val) => {
    if (!val) return "";
    if (Array.isArray(val)) return val[0] || "";
    if (typeof val === "object") {
      // common shapes: { path: "..."} or { url: "..." }
      if (val.path) return val.path;
      if (val.url) return val.url;
      // fallback to JSON string
      try { return JSON.stringify(val); } catch (e) { return String(val); }
    }
    return String(val);
  };

  const resolveImageUrl = (path) => {
    if (!path) return null;
    const p = String(path).trim();
    if (!p) return null;
    if (p.startsWith("http") || p.startsWith("data:")) return p;
    // strip leading slashes and prepend base
    const cleaned = p.replace(/^\/+/, "");
    return `${IMAGE_BASE_URL}${cleaned}`;
  };

  // Fetch fresh user data from server and update localStorage + state
  const fetchAndUpdateUser = useCallback(async () => {
    try {
      const storedUserRaw = localStorage.getItem("user");
      // if (!storedUserRaw) return;
       const storedUser = JSON.parse(storedUserRaw || "{}");
       const userId = storedUser._id || storedUser.id || localStorage.getItem("userId");
      // if (!userId) return;
      const res = await axios.get(`/api/user/${userId}`);
      const data = res.data;
      if (data && data.user) {
        localStorage.setItem("user", JSON.stringify(data.user));
        setUser(data.user);
      }
    } catch (err) {
      // don't spam users with errors when simply trying to refresh silently
      console.error("fetchAndUpdateUser error:", err);
      toastError("Failed to refresh user info.");
    }
  }, []);

  // Listen for 'userChanged' events dispatched after login/update so Nav updates immediately
  useEffect(() => {
    const onUserChanged = () => {
      try {
        const stored = JSON.parse(localStorage.getItem("user") || "{}");
        if (stored && (stored._id || stored.id)) {
          setUser(stored);
          // also refetch authoritative server version (in case server returned relative image path)
          fetchAndUpdateUser();
        } else {
          // fallback: try to fetch from server if local storage doesn't have complete user
          fetchAndUpdateUser();
        }
      } catch (e) {
        fetchAndUpdateUser();
      }
    };
    window.addEventListener('userChanged', onUserChanged);
    return () => window.removeEventListener('userChanged', onUserChanged);
  }, [fetchAndUpdateUser]);

  

  // Modal open logic
  const openProfileModal = async () => {
    setModalType("profile");
    setModalOpen(true);
    setFormErrors({});
    setFormValues({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      mobile: user.mobileNumber || '',
      email: user.email || '',
      position: user.role?.name || '',
      profileImage: user.profilepic ?resolveImageUrl(user.profilepic) :'', // file or dataURL to be set by modal
    });
  };

  

  const handleFormChange = (vals) => {
    setFormValues(vals);
  };

  // Simple validation for modal
  const validateForm = () => {
    let errors = {};
    if (!formValues.firstName) errors.firstName = "First name required";
    if (!formValues.email) errors.email = "Email required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Submit profile/org edit
  const handleFormSubmit = async () => {
    if (!validateForm()) return;
    try {
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = storedUser._id || storedUser.id || localStorage.getItem("userId");
      if (!userId) {
        toastError("User not found locally.");
        return;
      }
      const payload = {
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        mobileNumber: formValues.mobile,
        email: formValues.email,
        dob: formValues.birthDate,
        position: formValues.position,
      };
      let body = payload;
      let config = {};
      if (isFile(formValues.profileImage)) {
        const fd = new FormData();
        Object.entries(payload).forEach(([key, val]) => fd.append(key, val));
        fd.append("profilepic", formValues.profileImage);
        body = fd;
        config = { headers: { "Content-Type": "multipart/form-data" } };
      }
      const apiRes = await axios.put(`/api/user/${userId}`, body, config);

      const success = apiRes?.data && (apiRes.data.Status === 1 || apiRes.data.Status === true);
      if (success) {
        toastSuccess(`${modalType === "profile" ? "Profile" : "Organization"} updated successfully!`);
        setModalOpen(false);

        // If server returned updated user object, use it immediately; otherwise re-fetch
        if (apiRes.data.user) {
          localStorage.setItem("user", JSON.stringify(apiRes.data.user));
          setUser(apiRes.data.user);
          try { window.dispatchEvent(new Event('userChanged')); } catch (e) { }
        } else {
          // fetch fresh user from server to pick up server-side image paths
          await fetchAndUpdateUser();
        }
      } else {
        toastError(apiRes.data?.Message || "Update failed");
      }
    } catch (err) {
      console.error("handleFormSubmit error:", err);
      toastError("Update failed. Please try again!");
    }
  };

  // Logout logic
  const handleLogout = (e) => {
    try { if (e && e.preventDefault) e.preventDefault(); } catch (err) {}
    localStorage.clear();
    toastSuccess("Logout successful");
    navigate("/login");
  };

  const openLogoutModal = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setLogoutModalOpen(true);
  };

  // Use IMAGE_BASE_URL for profile picture and company logo (relative path)
  const rawProfile = normalizeApiPathValue(user.profilepic);
  const rawLogo = normalizeApiPathValue(user.business?.logo || user.business?.companyLogo);
  const profilePic =
    resolveImageUrl(rawProfile) || "/assets/images/s.png";

  const companyLogo =
    resolveImageUrl(rawLogo) || "/assets/images/s.png";

  const userName = `${user.firstName || ""} ${user.lastName || ""}`.trim();

  // Modal fields arrays
  const profileFields = [
    { name: "firstName", label: "First Name", type: "text" },
    { name: "lastName", label: "Last Name", type: "text" },
    { name: "mobile", label: "Mobile Number", type: "phone", readOnly: true },
    { name: "email", label: "Email Id", type: "email", readOnly: true },
    { name: "position", label: "Position", type: "text", readOnly: true },
    { name: "profileImage", label: "Upload Profile", type: "file", accept: "image/*" }
  ];
 

  return (
    <div className="navbar-header" style={{ padding: "20px 13px", height: "60px" }}>
      <div className="navbar-brand-box d-flex align-items-left">
        <a href="/" className="logo d-flex align-items-center">
          <img
            src={companyLogo}
            alt="Company Logo"
            style={{ height: 40, objectFit: 'contain', marginRight: 8 }}
          />
        </a>
        <button
          type="button"
          className="btn btn-sm mr-2 px-3 font-size-16 header-item waves-effect waves-dark mt-3"
          id="vertical-menu-btn"
          onClick={onMenuToggle}
        >
          <i className="fa fa-fw fa-bars"></i>
        </button>
      </div>

      <div className="d-flex align-items-center">
        <div className="dropdown d-inline-block ml-2">
          <button
            type="button"
            className="btn header-item waves-effect waves-light"
            id="page-header-user-dropdown"
            data-bs-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            <img
              className="rounded-circle header-profile-user"
              src={profilePic}
              alt="Header Avatar"
            />
            <span className="ms-2 fw-semibold">{userName}</span>
            <i className="mdi mdi-chevron-down d-none d-sm-inline-block"></i>
          </button>
          <div className="dropdown-menu dropdown-menu-right">
            <button
              type="button"
              className="dropdown-item d-flex align-items-center justify-content-between"
              onClick={openProfileModal}
            >
              <span>Profile</span>
            </button>
            <a
              href="#"
              onClick={openLogoutModal}
              className="dropdown-item d-flex align-items-center justify-content-between"
            >
              <span className="text-danger">Log Out</span>
            </a>
          </div>
        </div>
      </div>

      {/* Common Modal */}
      <CommonModal
        visible={modalOpen}
        title={"Edit Profile"}
        fields={profileFields}
        values={formValues}
        onChange={handleFormChange}
        onCancel={() => setModalOpen(false)}
        onSubmit={handleFormSubmit}
        errors={formErrors}
      />

      {logoutModalOpen && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content text-center p-4" style={{ borderRadius: 12, maxWidth: 480, margin: '0 auto' }}>
              <div style={{ marginBottom: 12 }}>
                <div style={{ width: 64, height: 64, background: '#fdecea', borderRadius: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="fa fa-sign-out text-danger" style={{ fontSize: 26 }} />
                </div>
              </div>
              <h5 className="mb-2">Log out ?</h5>
              <p className="text-muted">Are You Sure You Want To Log Out Of Your Account?</p>
              <div className="d-flex justify-content-center" style={{ gap: 8 }}>
                <button className="btn btn-secondary" onClick={() => setLogoutModalOpen(false)}>Cancel</button>
                <button className="btn btn-danger" onClick={() => { setLogoutModalOpen(false); handleLogout(); }}>Log Out</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Nav;